import * as React from 'react';
import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import AdminLayout, { AdminSection, AdminStatCard } from '../../components/AdminLayout';
import axiosInstance from '../../utils/axiosInstance';
import { getBusinessRoleLabel } from '../../utils/userProfile';

const emptyFilters = {
  search: '',
  status: '',
  issue_type: '',
};

const statusOptions = [
  { value: '', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'invited', label: 'Invited' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
];

const issueOptions = [
  { value: '', label: 'All issues' },
  { value: 'multi_membership', label: 'Multi-membership' },
  { value: 'duplicate_email', label: 'Duplicate email' },
  { value: 'unsupported_partner_role', label: 'Unsupported partner role' },
];

function formatDate(value) {
  if (!value) {
    return 'Never';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleString();
}

function issueLabel(issue) {
  if (issue === 'multi_membership') {
    return 'Multi-membership';
  }
  if (issue === 'duplicate_email') {
    return 'Duplicate email';
  }
  if (issue === 'unsupported_partner_role') {
    return 'Unsupported partner role';
  }
  return issue;
}

function issueColor(issue) {
  if (issue === 'multi_membership') {
    return 'error';
  }
  if (issue === 'unsupported_partner_role') {
    return 'secondary';
  }
  return 'warning';
}

function partnerLabel(membership) {
  return membership.display_name || membership.partner_code || membership.partner_id || 'Unknown partner';
}

export default function AdminAccountCompliancePage(props) {
  const [filters, setFilters] = useState(emptyFilters);
  const [state, setState] = useState({ loading: true, error: '', data: null });

  async function loadReport(nextFilters = filters) {
    setState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const response = await axiosInstance.get('/admin/account-compliance', { params: nextFilters });
      setState({ loading: false, error: '', data: response.data });
    } catch (error) {
      setState({
        loading: false,
        error: error?.response?.data?.message || 'Unable to load account compliance report.',
        data: null,
      });
    }
  }

  useEffect(() => {
    loadReport(emptyFilters);
  }, []);

  function updateFilter(field, value) {
    setFilters((current) => ({ ...current, [field]: value }));
  }

  function applyFilters(event) {
    event.preventDefault();
    loadReport(filters);
  }

  function resetFilters() {
    setFilters(emptyFilters);
    loadReport(emptyFilters);
  }

  const summary = state.data?.summary || {};
  const users = Array.isArray(state.data?.users) ? state.data.users : [];

  return (
    <AdminLayout
      props={props}
      breadcrumbItems={[
        { label: 'AMX xConsole', href: '/' },
        { label: 'Admin Dashboard', href: '/admin' },
        { label: 'Account Compliance' },
      ]}
      title="Account Compliance"
      subtitle="AMX-admin report for duplicate normalized emails, unsupported partner roles, and users with more than one partner membership."
    >
      <GridFallback>
        <AdminStatCard label="Non-compliant Users" value={summary.total_non_compliant_users || 0} />
        <AdminStatCard label="Duplicate Email Users" value={summary.duplicate_email_users || 0} />
        <AdminStatCard label="Multi-membership Users" value={summary.multi_membership_users || 0} />
        <AdminStatCard label="Unsupported Role Users" value={summary.unsupported_partner_role_users || 0} />
      </GridFallback>

      <AdminSection title="Reports" subtitle="Search by email, name, partner, role, status, or Cognito sub.">
        <Stack component="form" direction={{ xs: 'column', lg: 'row' }} spacing={1.5} onSubmit={applyFilters}>
          <TextField
            label="Search"
            value={filters.search}
            onChange={(event) => updateFilter('search', event.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            select
            label="Status"
            value={filters.status}
            onChange={(event) => updateFilter('status', event.target.value)}
            size="small"
            sx={{ minWidth: { xs: '100%', lg: 180 } }}
          >
            {statusOptions.map((option) => (
              <MenuItem key={option.value || 'all'} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Issue"
            value={filters.issue_type}
            onChange={(event) => updateFilter('issue_type', event.target.value)}
            size="small"
            sx={{ minWidth: { xs: '100%', lg: 220 } }}
          >
            {issueOptions.map((option) => (
              <MenuItem key={option.value || 'all'} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Button type="submit" variant="contained" startIcon={<SearchRoundedIcon />}>
            Search
          </Button>
          <Button type="button" variant="outlined" startIcon={<RestartAltRoundedIcon />} onClick={resetFilters}>
            Reset
          </Button>
        </Stack>
      </AdminSection>

      {state.error ? <Alert severity="error">{state.error}</Alert> : null}

      {state.loading ? (
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress size={24} />
          <Typography>Loading account compliance report...</Typography>
        </Stack>
      ) : (
        <AdminSection title="Non-compliant Users" subtitle="Admin actions are intentionally not enabled from this report.">
          {users.length === 0 ? (
            <Alert severity="success">No non-compliant users match the current filters.</Alert>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 1180 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Issues</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last Login</TableCell>
                    <TableCell>Memberships</TableCell>
                    <TableCell>Suggested Remediation</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.cognito_sub} hover>
                      <TableCell sx={{ minWidth: 260 }}>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {user.email || 'No email'}
                          </Typography>
                          <Typography variant="body2">{user.full_name || 'No name'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.cognito_sub}
                          </Typography>
                          {user.normalized_email ? (
                            <Typography variant="caption" color="text.secondary">
                              normalized: {user.normalized_email}
                            </Typography>
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ minWidth: 190 }}>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap">
                          {(user.issue_types || []).map((issue) => (
                            <Chip
                              key={issue}
                              size="small"
                              color={issueColor(issue)}
                              label={issueLabel(issue)}
                            />
                          ))}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {user.duplicate_email_count || 0} email users | {user.partner_count || 0} partners
                        </Typography>
                      </TableCell>
                      <TableCell>{user.status}</TableCell>
                      <TableCell>{formatDate(user.last_login_at)}</TableCell>
                      <TableCell sx={{ minWidth: 300 }}>
                        <Stack spacing={1}>
                          {(user.memberships || []).length ? (
                            user.memberships.map((membership) => (
                              <Box key={`${user.cognito_sub}-${membership.partner_id}`}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {partnerLabel(membership)}
                                </Typography>
                                <Stack direction="row" spacing={0.75} flexWrap="wrap">
                                  <Chip size="small" variant="outlined" label={getBusinessRoleLabel(membership.role, 'No role')} />
                                  <Chip size="small" variant="outlined" label={membership.partner_code || membership.partner_id} />
                                </Stack>
                              </Box>
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary">No partner memberships</Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ minWidth: 300 }}>
                        <Stack spacing={0.75}>
                          {(user.suggested_remediation_options || []).map((option) => (
                            <Box key={option.code}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {option.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.description}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </AdminSection>
      )}
    </AdminLayout>
  );
}

function GridFallback({ children }) {
  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
      {React.Children.map(children, (child, index) => (
        <Box key={index} sx={{ flex: 1, minWidth: 0 }}>{child}</Box>
      ))}
    </Stack>
  );
}
